/**
 * Tenant-admin lifecycle controls — Start review, Approve, Reject.
 * Renders alongside the snapshot panel on the admin review page.
 */

import { useState } from "react";

import { AdminOfferComposer } from "@/components/admin/AdminOfferComposer";
import { AdminOfferHistory } from "@/components/admin/AdminOfferHistory";
import { ClarificationComposer } from "@/components/admin/ClarificationComposer";
import {
  approveEstimate,
  rejectEstimate,
  resumeReview,
  startReviewEstimate,
} from "@starz/shared/api/sdk.gen";
import type { Estimate } from "@starz/shared/api/types.gen";

interface Props {
  estimate: Estimate;
  /** The latest snapshot subtotal, used to pre-fill the offer composer
   * (offer is admin override of the snapshot total). */
  defaultOfferCents?: number;
  onUpdated: (updated: Estimate) => void;
}

export function AdminReviewActions({ estimate, defaultOfferCents, onUpdated }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [askOpen, setAskOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  async function onResume() {
    setBusy("resume");
    setError(null);
    const result = await resumeReview({ estimateId: estimate.estimate_id });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onUpdated(result.data);
  }

  async function onStart() {
    setBusy("start");
    setError(null);
    const result = await startReviewEstimate({
      estimateId: estimate.estimate_id,
    });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onUpdated(result.data);
  }

  async function onApprove() {
    if (!window.confirm("Approve this estimate? The customer will be notified.")) return;
    setBusy("approve");
    setError(null);
    const result = await approveEstimate({ estimateId: estimate.estimate_id });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    onUpdated(result.data);
  }

  async function onReject() {
    if (!rejectReason.trim()) {
      setError("Reason is required.");
      return;
    }
    setBusy("reject");
    setError(null);
    const result = await rejectEstimate({
      estimateId: estimate.estimate_id,
      body: { reason: rejectReason.trim() },
    });
    setBusy(null);
    if ("error" in result) {
      setError(result.error.error.message);
      return;
    }
    setRejectOpen(false);
    setRejectReason("");
    onUpdated(result.data);
  }

  // Phase 17 — even on approved/rejected/expired we still surface the
  // offer panel (admins can adjust a discount on an approved quote up
  // until the customer pays). Only return null for fully closed paths.
  const showActionButtons =
    estimate.status !== "approved" &&
    estimate.status !== "rejected" &&
    estimate.status !== "expired";

  return (
    <section
      aria-label="Review actions"
      className="mb-6 rounded-xl border border-starz-gold/20 bg-white p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-widest text-warm-gray">Review actions</p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setOfferOpen((s) => !s)}
            disabled={busy != null}
            className="min-h-[44px] rounded-lg border border-starz-gold/40 bg-starz-cream px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-navy disabled:opacity-60"
          >
            {estimate.current_offer ? "Update offer" : "Make offer"}
          </button>
          {showActionButtons && estimate.status === "submitted" && (
            <button
              type="button"
              onClick={onStart}
              disabled={busy != null}
              className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white disabled:opacity-60"
            >
              {busy === "start" ? "Starting…" : "Start review"}
            </button>
          )}
          {showActionButtons && (estimate.status === "in_review" || estimate.status === "info_requested") && (
            <>
              {estimate.status === "info_requested" && (
                <button
                  type="button"
                  onClick={onResume}
                  disabled={busy != null}
                  className="min-h-[44px] rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white disabled:opacity-60"
                >
                  {busy === "resume" ? "Resuming…" : "Resume review"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setAskOpen((s) => !s)}
                disabled={busy != null}
                className="min-h-[44px] rounded-lg border border-violet-300 bg-violet-50 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-violet-900 disabled:opacity-60"
              >
                Ask the customer
              </button>
              <button
                type="button"
                onClick={onApprove}
                disabled={busy != null}
                className="min-h-[44px] rounded-lg bg-emerald-600 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white disabled:opacity-60"
              >
                {busy === "approve" ? "Approving…" : "Approve"}
              </button>
              <button
                type="button"
                onClick={() => setRejectOpen((s) => !s)}
                disabled={busy != null}
                className="min-h-[44px] rounded-lg border border-rose-300 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-rose-700 disabled:opacity-60"
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
      {askOpen && (
        <div className="mt-4 border-t border-starz-gold/15 pt-4">
          <ClarificationComposer
            estimate={estimate}
            onAsked={(updated) => {
              setAskOpen(false);
              onUpdated(updated);
            }}
          />
        </div>
      )}
      {offerOpen && (
        <div className="mt-4 space-y-3 border-t border-starz-gold/15 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-warm-gray">
            Negotiation offer
          </p>
          <AdminOfferComposer
            estimate={estimate}
            defaultFinalCents={
              estimate.current_offer?.final_cents ??
              defaultOfferCents ??
              estimate.last_subtotal_high_cents ??
              0
            }
            onSaved={(updated) => {
              setOfferOpen(false);
              onUpdated(updated);
            }}
          />
          {(estimate.offer_history?.length ?? 0) > 0 ? (
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-widest text-warm-gray">
                Prior offers (admin-only)
              </p>
              <AdminOfferHistory history={estimate.offer_history ?? []} />
            </div>
          ) : null}
        </div>
      )}
      {rejectOpen && (
        <div className="mt-4 space-y-3 border-t border-starz-gold/15 pt-4">
          <label className="block text-xs font-semibold uppercase tracking-widest text-starz-charcoal">
            Reason for rejection
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full rounded-lg border border-starz-gold/30 bg-white p-3 text-sm shadow-inner focus:border-starz-gold focus:outline-none"
            placeholder="Explain to the customer what's blocking us…"
          />
          <button
            type="button"
            onClick={onReject}
            disabled={busy != null}
            className="min-h-[44px] rounded-lg bg-rose-700 px-5 py-3 text-sm font-semibold uppercase tracking-wide text-white disabled:opacity-60"
          >
            {busy === "reject" ? "Rejecting…" : "Confirm rejection"}
          </button>
        </div>
      )}
      {error && (
        <p className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}
    </section>
  );
}
